import { useEffect, useState } from 'react';
import { Archive, Image, Save } from 'lucide-react';
import { api } from '../api.js';
import { useGuild } from './GuildLayout.jsx';

export default function ConfigMirrors() {
  const { guildId, channels } = useGuild();
  const [cfg, setCfg] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    api.getConfig(guildId).then(c => setCfg({
      archive_channel_id:    c.archive_channel_id    ?? null,
      media_dump_channel_id: c.media_dump_channel_id ?? null,
      media_dump_excluded:   c.media_dump_excluded   ?? null,
    }));
  }, [guildId]);

  if (!cfg) return <div className="card"><div className="skel skel-line med" /><div className="skel skel-line short" /></div>;

  const excludedSet = (() => {
    try { return new Set(cfg.media_dump_excluded ? JSON.parse(cfg.media_dump_excluded) : []); }
    catch { return new Set(); }
  })();

  function update(k, v) {
    setCfg(c => ({ ...c, [k]: v }));
    setDirty(true);
  }

  function toggleExcluded(channelId) {
    const next = new Set(excludedSet);
    if (next.has(channelId)) next.delete(channelId); else next.add(channelId);
    update('media_dump_excluded', next.size ? JSON.stringify([...next]) : null);
  }

  async function save() {
    setSaving(true);
    try {
      await api.putConfig(guildId, cfg);
      setDirty(false);
      setToast({ type: 'success', msg: 'Saved' });
    } catch (err) {
      setToast({ type: 'error', msg: err.message });
    } finally {
      setSaving(false);
      setTimeout(() => setToast(null), 2500);
    }
  }

  const textChannels = channels.filter(c => c.type === 0 || c.type === 5);

  return (
    <>
      <h2 style={{ marginTop: 0 }}>Mirror &amp; Archive</h2>
      <p className="muted" style={{ marginTop: '-.25rem' }}>Centralize media into one channel and let mods archive important messages.</p>

      <div className="card">
        <h3 style={{ marginTop: 0 }}><Image size={16} strokeWidth={2.5} style={{ verticalAlign: 'middle', marginRight: 6 }} /> Media dump</h3>
        <p className="muted" style={{ marginTop: '-.5rem', fontSize: 13 }}>Bot re-uploads every attachment posted in any non-excluded channel to your media-dump channel. Includes a header with the original author + channel + jump link.</p>
        <div className="field">
          <label>Media-dump channel</label>
          <select value={cfg.media_dump_channel_id || ''} onChange={e => update('media_dump_channel_id', e.target.value || null)}>
            <option value="">— disabled —</option>
            {textChannels.map(c => <option key={c.id} value={c.id}>#{c.name}</option>)}
          </select>
        </div>
        {cfg.media_dump_channel_id && (
          <div className="field">
            <label>Excluded channels <span className="dim" style={{ textTransform: 'none', letterSpacing: 'normal', fontWeight: 400 }}>· media in these won't be mirrored</span></label>
            <div className="event-grid">
              {textChannels
                .filter(c => c.id !== cfg.media_dump_channel_id)
                .map(c => (
                  <label key={c.id} className="event-toggle">
                    <input
                      type="checkbox"
                      checked={excludedSet.has(c.id)}
                      onChange={() => toggleExcluded(c.id)}
                    />
                    <span>#{c.name}</span>
                  </label>
                ))}
            </div>
          </div>
        )}
      </div>

      <div className="card">
        <h3 style={{ marginTop: 0 }}><Archive size={16} strokeWidth={2.5} style={{ verticalAlign: 'middle', marginRight: 6 }} /> Archive</h3>
        <p className="muted" style={{ marginTop: '-.5rem', fontSize: 13 }}>Mods can right-click any message → <strong>Apps → Save to archive</strong>. Bot mirrors the message (and any attachments) to this channel with author info + a jump link back to the original.</p>
        <div className="field" style={{ marginBottom: 0 }}>
          <label>Archive channel</label>
          <select value={cfg.archive_channel_id || ''} onChange={e => update('archive_channel_id', e.target.value || null)}>
            <option value="">— disabled —</option>
            {textChannels.map(c => <option key={c.id} value={c.id}>#{c.name}</option>)}
          </select>
          <p className="dim" style={{ fontSize: 12, marginTop: 6 }}>
            Requires <span className="tag">Manage Messages</span> permission on the mod's role for the context-menu command to appear.
          </p>
        </div>
      </div>

      <div className="row actions">
        <button disabled={!dirty || saving} onClick={save}>
          <Save size={14} strokeWidth={2.5} /> {saving ? 'Saving…' : 'Save changes'}
        </button>
      </div>

      {toast && <div className={`toast ${toast.type}`}>{toast.msg}</div>}
    </>
  );
}
